import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { dayLabel, monthTicks, periodState, rangeLabel, shiftRange, slotTargets, snapRange, snapThreshold, toDay, toIso } from './dates';
import { iterationProgress, sprintProgress, sprintShort } from './colors';

// Frise de la roadmap : une ligne par itération (les sprints en segments dans la barre), les jalons en losanges, un
// trait « aujourd'hui ». On glisse une barre pour la déplacer (ses sprints suivent), ses bords pour l'étirer ; près d'un
// créneau, elle s'y colle. Un clic ouvre l'itération ; l'itération ouverte montre ses sprints, déplaçables un à un.
// « Ajusté » (par défaut) cale l'échelle sur les itérations affichées pour qu'elles remplissent la largeur, sans
// défilement ; la colonne des noms s'élargit jusqu'au plus long (dans la limite d'un tiers de la frise).

export const ZOOM_DAYS = { quarter: 100, semester: 190, year: 380 };
const LABEL_MIN = 208;
const LABEL_MAX = 380;
const ROW_H = 46;
const LANE_H = 38;
const MILESTONE_LANE_H = 22;
const DRAG_START_PX = 4;

const STATE_LABEL = { current: 'en cours', upcoming: 'à venir', done: 'terminée', undated: 'sans dates' };
const STATE_TONE = {
  current: 'bg-emerald-50 text-emerald-700', upcoming: 'bg-slate-100 text-slate-600',
  done: 'bg-slate-50 text-slate-400', undated: 'bg-amber-50 text-amber-700',
};

const span = (item) => ({ start: toDay(item.start_date), end: toDay(item.end_date) });

let canvas = null;
const textWidth = (text, font) => {
  try {
    canvas = canvas || document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return String(text).length * 7.5;
    context.font = font;
    return context.measureText(text).width;
  } catch {
    return String(text).length * 7.5; // pas de canevas (tests) : une estimation suffit
  }
};

/** Largeur de la colonne des noms : le plus long nom d'itération en entier, dans la limite d'un tiers de la frise. */
export const labelWidth = (names, viewport) => {
  const longest = Math.max(0, ...names.map((name) => textWidth(name || '', '500 14px Inter, ui-sans-serif, system-ui, sans-serif')));
  const room = Math.min(LABEL_MAX, Math.max(LABEL_MIN, viewport / 3));
  return Math.round(Math.min(room, Math.max(LABEL_MIN, longest + 48)));
};

/** Bornes de la frise : en « Ajusté », juste les itérations et jalons (et aujourd'hui s'il est proche) ; sinon une
 * fenêtre de la durée du zoom au moins. */
export const timelineBounds = (days, today, zoom) => {
  const all = days.filter((day) => day != null);
  if (zoom !== 'fit' || !all.length) {
    const list = [today, ...all];
    const from = Math.min(...list) - 14;
    return { from, to: Math.max(Math.max(...list) + 30, from + (ZOOM_DAYS[zoom] || ZOOM_DAYS.semester)) };
  }
  let first = Math.min(...all);
  let last = Math.max(...all);
  if (today >= first - 60 && today <= last + 60) { first = Math.min(first, today); last = Math.max(last, today); }
  const pad = Math.max(4, Math.round((last - first) * 0.03));
  return { from: first - pad, to: last + pad };
};

// Jalons répartis sur quelques lignes pour que leurs libellés ne se chevauchent pas.
const milestoneLanes = (milestones, x) => {
  const ends = [];
  return milestones.map((milestone) => {
    const left = x(toDay(milestone.milestone_date));
    let lane = ends.findIndex((end) => end < left);
    if (lane === -1) lane = ends.length < 3 ? ends.length : ends.indexOf(Math.min(...ends));
    ends[lane] = left + 150;
    return { milestone, left, lane };
  });
};

const Gantt = ({
  iterations, milestones, slots, today, zoom, colors, selection, recenter,
  onSelect, onMoveIteration, onPatchSprint, onMoveMilestone, onOpenMilestone, locked = false, expanded = null,
}) => {
  const scroller = useRef(null);
  const drag = useRef(null);
  const lockedRef = useRef(locked); // roadmap tenue par un autre rôle : on ouvre, on ne déplace pas (sauf les sprints)
  lockedRef.current = locked;
  const [viewport, setViewport] = useState(900);
  const [preview, setPreview] = useState(null);
  const shown = useRef(null);
  const show = useCallback((value) => { shown.current = value; setPreview(value); }, []);

  useLayoutEffect(() => {
    const node = scroller.current;
    if (!node) return undefined;
    const measure = () => setViewport(node.clientWidth || 900);
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, []);

  const bounds = useMemo(() => {
    const days = [];
    iterations.forEach((it) => { const { start, end } = span(it); days.push(start, end); });
    // En « Ajusté », un jalon atteint avant la première itération affichée n'étire pas la frise (il reste à sa place).
    const first = Math.min(...days.filter((day) => day != null));
    milestones.forEach((m) => {
      const day = toDay(m.milestone_date);
      if (zoom !== 'fit' || m.status !== 'done' || !(day < first)) days.push(day);
    });
    return timelineBounds(days, today, zoom);
  }, [iterations, milestones, today, zoom]);

  const LABEL_W = useMemo(() => labelWidth(iterations.map((it) => it.name), viewport), [iterations, viewport]);
  const fit = zoom === 'fit';
  const ppd = Math.max(0.8, (viewport - LABEL_W) / (fit ? bounds.to - bounds.from + 1 : ZOOM_DAYS[zoom]));
  const width = (bounds.to - bounds.from + 1) * ppd;
  const x = useCallback((day) => (day - bounds.from) * ppd, [bounds.from, ppd]);
  const targets = useMemo(() => slotTargets(slots), [slots]);
  const months = useMemo(() => monthTicks(bounds.from, bounds.to), [bounds]);
  const slotBands = useMemo(() => slots.map((slot) => ({ ...slot, ...span(slot) }))
    .filter((s) => s.start != null && s.end != null && s.end >= bounds.from && s.start <= bounds.to), [slots, bounds]);
  const inView = useMemo(() => milestones.filter((m) => {
    const day = toDay(m.milestone_date);
    return day != null && day >= bounds.from && day <= bounds.to;
  }), [milestones, bounds]);
  const lanes = useMemo(() => milestoneLanes(inView, x), [inView, x]);
  const milestoneRowH = 12 + Math.max(1, ...lanes.map((l) => l.lane + 1)) * MILESTONE_LANE_H;

  // Aujourd'hui au quart gauche de la zone visible : au chargement, au changement de zoom, sur « Aujourd'hui ». Quand
  // seule la largeur change (panneau ouvert ou fermé), on garde plutôt l'itération ouverte en vue.
  const centered = useRef(null);
  useLayoutEffect(() => {
    const node = scroller.current;
    if (!node) return;
    const visible = viewport - LABEL_W;
    if (fit) { node.scrollLeft = 0; return; } // tout tient dans la largeur
    const key = `${zoom}|${recenter}`;
    if (centered.current !== key) {
      centered.current = key;
      node.scrollLeft = Math.max(0, x(today) - visible * 0.25);
      return;
    }
    const open = iterations.find((it) => it.id === selection?.iterationId);
    const start = open && toDay(open.start_date);
    if (start == null) return;
    const left = x(start);
    if (left < node.scrollLeft || left > node.scrollLeft + visible - 40) node.scrollLeft = Math.max(0, left - visible * 0.1);
  }, [zoom, recenter, viewport]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Glisser : barre d'itération, sprint de l'itération ouverte, jalon.
  const begin = (event, item, mode) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current = { originX: event.clientX, item, mode, moved: false };
  };

  useEffect(() => {
    const move = (event) => {
      const current = drag.current;
      if (!current || (lockedRef.current && current.item.type !== 'sprint')) return; // les sprints restent à l'équipe
      const dx = event.clientX - current.originX;
      if (!current.moved && Math.abs(dx) < DRAG_START_PX) return;
      current.moved = true;
      const { item, mode } = current;
      let next = shiftRange(item.start, item.end, mode, Math.round(dx / ppd));
      if (item.type !== 'milestone') next = snapRange(next.start, next.end, mode, targets, snapThreshold(ppd));
      show({ key: item.key, ...next });
    };
    const up = () => {
      const current = drag.current;
      const last = shown.current;
      drag.current = null;
      if (!current) return;
      const { item } = current;
      if (!current.moved) {
        show(null);
        item.onClick();
        return;
      }
      if (!last || last.key !== item.key || (last.start === item.start && last.end === item.end)) {
        show(null);
        return;
      }
      Promise.resolve(item.onCommit(last, current.mode)).finally(() => { if (shown.current?.key === item.key) show(null); });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [ppd, targets, show]);

  const live = (key, start, end) => (preview?.key === key ? { start: preview.start, end: preview.end, dragging: true } : { start, end, dragging: false });

  const handles = (item) => (
    <>
      <span onPointerDown={(e) => begin(e, item, 'start')} title="Étirer"
        className="absolute inset-y-0 -left-1 z-10 w-2.5 cursor-ew-resize rounded-l-md opacity-0 transition group-hover:opacity-100">
        <span className="absolute inset-y-1.5 left-1 w-0.5 rounded-full bg-slate-500/50" />
      </span>
      <span onPointerDown={(e) => begin(e, item, 'end')} title="Étirer"
        className="absolute inset-y-0 -right-1 z-10 w-2.5 cursor-ew-resize rounded-r-md opacity-0 transition group-hover:opacity-100">
        <span className="absolute inset-y-1.5 right-1 w-0.5 rounded-full bg-slate-500/50" />
      </span>
    </>
  );

  const dragTip = (start, end) => (
    <span className="pointer-events-none absolute -top-7 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white shadow">
      {rangeLabel(start, end)}
    </span>
  );

  const renderBar = (iteration) => {
    const color = colors.get(iteration.id);
    const own = span(iteration);
    if (own.start == null || own.end == null) return null;
    const key = `iteration:${iteration.id}`;
    const { start, end, dragging } = live(key, own.start, own.end);
    const shift = start - own.start;
    const moving = dragging && end - start === own.end - own.start;
    const selected = selection?.iterationId === iteration.id;
    const sprints = iteration.sprints || [];
    const item = {
      key, type: 'iteration', start: own.start, end: own.end,
      onClick: () => onSelect({ iterationId: iteration.id }),
      onCommit: (next, mode) => onMoveIteration(iteration, {
        start: toIso(next.start), end: toIso(next.end), shift: mode === 'move' ? next.start - own.start : 0,
      }),
    };
    return (
      <div role="button" tabIndex={0} title={`${iteration.name} · ${rangeLabel(own.start, own.end)}${iteration.description ? `\n${iteration.description}` : ''}`}
        onPointerDown={(e) => begin(e, item, 'move')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.onClick(); } }}
        className={`group absolute top-[9px] h-7 cursor-grab touch-none select-none rounded-md border ${color.line} ${color.soft} outline-none transition-shadow focus-visible:ring-2 ${color.ring}
          ${selected ? `ring-2 ring-offset-1 ${color.ring}` : 'hover:shadow-md'} ${dragging ? 'z-20 cursor-grabbing shadow-lg' : ''}`}
        style={{ left: x(start), width: Math.max(6, (end - start + 1) * ppd) }}>
        {sprints.length ? sprints.map((sprint, index) => {
          const s = span(sprint);
          if (s.start == null || s.end == null) return null;
          const from = s.start + (moving ? shift : 0);
          const left = (from - start) * ppd;
          const segment = Math.max(2, (s.end - s.start + 1) * ppd - 2);
          const current = periodState(s.start, s.end, today) === 'current';
          return (
            <span key={sprint.id} className={`absolute inset-y-[3px] overflow-hidden rounded-[4px] bg-white/60 ${current ? `ring-1 ring-inset ${color.ring}` : ''}`}
              style={{ left: left + 1, width: segment }}>
              <span className={`absolute inset-y-0 left-0 ${color.fill} ${current ? 'opacity-90' : 'opacity-60'}`} style={{ width: `${sprintProgress(sprint) * 100}%` }} />
              {segment >= 30 && (
                <span className={`relative block truncate px-1 text-center text-[10px] font-semibold leading-[22px] ${color.text}`}>{sprintShort(sprint, index)}</span>
              )}
            </span>
          );
        }) : (
          <span className={`absolute inset-y-[3px] left-[3px] rounded-[4px] ${color.fill} opacity-60`} style={{ width: `calc(${iterationProgress(iteration) * 100}% - 6px)` }} />
        )}
        {!locked && handles(item)}
        {dragging && dragTip(start, end)}
      </div>
    );
  };

  // Itération ouverte : ses sprints en pastilles, chacun déplaçable et étirable.
  const renderSprintLane = (iteration) => {
    const color = colors.get(iteration.id);
    return (iteration.sprints || []).map((sprint, index) => {
      const own = span(sprint);
      if (own.start == null || own.end == null) return null;
      const key = `sprint:${sprint.id}`;
      const { start, end, dragging } = live(key, own.start, own.end);
      const item = {
        key, type: 'sprint', start: own.start, end: own.end,
        onClick: () => onSelect({ iterationId: iteration.id, sprintId: sprint.id }),
        onCommit: (next) => onPatchSprint(sprint.id, { start_date: toIso(next.start), end_date: toIso(next.end) }),
      };
      const chosen = selection?.sprintId === sprint.id;
      const current = periodState(own.start, own.end, today) === 'current';
      return (
        <div key={sprint.id} role="button" tabIndex={0} onPointerDown={(e) => begin(e, item, 'move')}
          onKeyDown={(e) => { if (e.key === 'Enter') item.onClick(); }}
          title={`${sprint.name} · ${rangeLabel(own.start, own.end)}${sprint.vision ? `\n${sprint.vision}` : ''}`}
          className={`group absolute flex h-7 cursor-grab touch-none select-none items-center gap-1.5 overflow-hidden rounded-full border bg-white px-2 text-[11px] shadow-sm
            ${chosen ? `ring-2 ${color.ring} border-transparent` : 'border-slate-200 hover:border-slate-300'} ${dragging ? 'z-20 cursor-grabbing shadow-lg' : ''}`}
          style={{ top: ROW_H - 4, left: x(start) + 1, width: Math.max(14, (end - start + 1) * ppd - 2) }}>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${current ? color.dot : 'bg-slate-300'}`} />
          <span className="shrink-0 font-semibold text-slate-700">{sprintShort(sprint, index)}</span>
          <span className="truncate text-slate-500">{sprint.vision}</span>
          {handles(item)}
          {dragging && dragTip(start, end)}
        </div>
      );
    });
  };

  return (
    <div ref={scroller} className={`relative ${fit ? 'overflow-x-hidden' : 'overflow-x-auto overscroll-x-contain'} overflow-y-hidden rounded-xl border border-slate-200 bg-white`}>
      <div className="relative" style={{ width: LABEL_W + width }}>
        {/* Quadrillage : mois, créneaux un sur deux, jalons, aujourd'hui. */}
        <div className="pointer-events-none absolute inset-y-0" style={{ left: LABEL_W, width }}>
          {slotBands.map((slot, index) => (index % 2 === 0 ? (
            <div key={slot.id} className="absolute inset-y-0 bg-violet-50/50" style={{ left: x(slot.start), width: (slot.end - slot.start + 1) * ppd }} />
          ) : null))}
          {months.map((tick) => <div key={tick.day} className="absolute inset-y-0 border-l border-slate-100" style={{ left: x(tick.day) }} />)}
          {lanes.map(({ milestone, left }) => (
            <div key={milestone.id} className="absolute border-l border-dashed border-rose-200" style={{ left: left + ppd / 2, top: 52, bottom: 0 }} />
          ))}
          <div className="absolute inset-y-0 z-10 w-px bg-emerald-500" style={{ left: x(today) + ppd / 2 }}>
            <span className="absolute left-1/2 top-1 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-500 px-1.5 py-px text-[10px] font-semibold text-white">
              Aujourd'hui
            </span>
          </div>
        </div>

        {/* En-tête : mois et créneaux. */}
        <div className="flex h-[52px] border-b border-slate-100">
          <div className="sticky left-0 z-20 shrink-0 border-r border-slate-100 bg-white" style={{ width: LABEL_W }} />
          <div className="relative" style={{ width }}>
            {months.map((tick) => (
              <span key={tick.day} className="absolute top-6 pl-1.5 text-[11px] font-medium capitalize text-slate-500" style={{ left: x(tick.day) }}>{tick.label}</span>
            ))}
            {slotBands.map((slot) => (
              <span key={slot.id} className="absolute bottom-1 truncate px-1 text-[10px] text-violet-400"
                style={{ left: x(slot.start), width: (slot.end - slot.start + 1) * ppd }} title={`${slot.name} · ${rangeLabel(slot.start, slot.end)}`}>
                {slot.name}
              </span>
            ))}
          </div>
        </div>

        {/* Jalons (la ligne n'apparaît que s'il y en a). */}
        {inView.length > 0 && <div className="flex border-b border-slate-100" style={{ height: milestoneRowH }}>
          <div className="sticky left-0 z-20 flex shrink-0 items-center border-r border-slate-100 bg-white px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400" style={{ width: LABEL_W }}>
            Jalons
          </div>
          <div className="relative" style={{ width }}>
            {lanes.map(({ milestone, lane }) => {
              const own = toDay(milestone.milestone_date);
              const key = `milestone:${milestone.id}`;
              const { start, dragging } = live(key, own, own);
              const done = milestone.status === 'done';
              const late = !done && own < today;
              const item = {
                key, type: 'milestone', start: own, end: own,
                onClick: () => onOpenMilestone(milestone),
                onCommit: (next) => onMoveMilestone(milestone.id, toIso(next.start)),
              };
              return (
                <button key={milestone.id} type="button" onPointerDown={(e) => begin(e, item, 'move')}
                  onKeyDown={(e) => { if (e.key === 'Enter') item.onClick(); }}
                  title={`${milestone.name} · ${dayLabel(own)}${done ? ' · atteint' : late ? ' · en retard' : ''}`}
                  className={`absolute flex max-w-[150px] cursor-grab touch-none select-none items-center gap-1.5 rounded-full py-0.5 pr-2 text-[11px] font-medium hover:bg-white hover:shadow-sm ${dragging ? 'z-20 cursor-grabbing bg-white shadow' : ''}`}
                  style={{ left: x(start) + ppd / 2 - 6, top: 6 + lane * MILESTONE_LANE_H }}>
                  <span className={`h-3 w-3 shrink-0 rotate-45 rounded-[2px] ${done ? 'bg-emerald-500' : late ? 'bg-rose-500' : 'bg-rose-400'}`} />
                  <span className={`truncate ${done ? 'text-slate-400 line-through' : late ? 'text-rose-700' : 'text-slate-700'}`}>{milestone.name}</span>
                  {dragging && dragTip(start, start)}
                </button>
              );
            })}
          </div>
        </div>}

        {/* Itérations. */}
        {iterations.map((iteration) => {
          const color = colors.get(iteration.id);
          const { start, end } = span(iteration);
          const state = periodState(start, end, today);
          const selected = selection?.iterationId === iteration.id;
          const open = selected || Boolean(expanded?.has(iteration.id)); // sprints visibles : itération ouverte ou en cours
          return (
            <div key={iteration.id} className={`flex border-b border-slate-50 ${state === 'done' && !selected ? 'opacity-55' : ''}`}
              style={{ height: open ? ROW_H + LANE_H : ROW_H }}>
              <button type="button" onClick={() => onSelect(selected ? null : { iterationId: iteration.id })} title={iteration.name}
                className={`sticky left-0 z-20 flex shrink-0 items-start gap-2 overflow-hidden border-r border-slate-100 px-3 pt-2 text-left transition ${selected ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'}`}
                style={{ width: LABEL_W }}>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${color.dot}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{iteration.name}</span>
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className="truncate">{start != null && end != null ? rangeLabel(start, end) : 'Dates à préciser'}</span>
                    <span className={`shrink-0 rounded-full px-1.5 text-[10px] font-medium leading-4 ${STATE_TONE[state]}`}>{STATE_LABEL[state]}</span>
                  </span>
                </span>
              </button>
              <div className="relative" style={{ width }}>
                {renderBar(iteration)}
                {open && renderSprintLane(iteration)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Gantt;
